import { useRef, useState } from 'react';
import { ArrowDown, ArrowUp, MoreVertical, Pencil, Trash2 } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover';

export interface AutomationStepMenuProps {
  /** 1-based position, for the trigger's accessible name ("Step 2 actions"). */
  stepNumber: number;
  canMoveUp: boolean;
  canMoveDown: boolean;
  /** Opens the step's editor or settings, which takes focus (see below). */
  onEdit: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: () => void;
}

const ITEM = 'w-full text-left px-4 py-2.5 text-sm hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-transparent';

/**
 * The ⋮ menu on an SMS or email automation step: Edit Details, Move Up, Move
 * Down, Delete.
 *
 * - Portalled, so the step card's overflow-hidden cannot clip it.
 * - Each menu owns its open state. A shared "which step is open" index let one
 *   step's late close clear the menu another step had just opened.
 * - Modal, like the full-screen overlay it replaces: a click outside only
 *   closes the menu and never reaches what is under it, and Tab stays inside.
 * - flex-col, because the popper sizes to max-content and inline-block buttons
 *   measure side by side, which made the menu about four times too wide.
 * - Closing returns focus to the ⋮, except after Edit Details: the editor or
 *   modal it opens keeps focus.
 */
export function AutomationStepMenu({
  stepNumber,
  canMoveUp,
  canMoveDown,
  onEdit,
  onMoveUp,
  onMoveDown,
  onDelete,
}: AutomationStepMenuProps) {
  const [open, setOpen] = useState(false);
  const skipFocusReturn = useRef(false);

  const choose = (action: () => void) => () => {
    setOpen(false);
    action();
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="text-slate-400 hover:text-slate-700"
          aria-label={`Step ${stepNumber} actions`}
          onClick={(e) => e.stopPropagation()}
        >
          <MoreVertical className="h-5 w-5" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        onCloseAutoFocus={(e) => {
          if (skipFocusReturn.current) {
            e.preventDefault();
            skipFocusReturn.current = false;
          }
        }}
        className="flex w-auto min-w-[160px] flex-col overflow-hidden rounded-lg p-0 shadow-lg"
      >
        <button
          type="button"
          className={ITEM}
          onClick={() => {
            skipFocusReturn.current = true;
            choose(onEdit)();
          }}
        >
          <Pencil className="inline h-3.5 w-3.5 mr-2" />Edit Details
        </button>
        {/* Same move as the drag, for anyone not using a mouse or working on a
            touch screen. */}
        <button type="button" className={ITEM} disabled={!canMoveUp} onClick={choose(onMoveUp)}>
          <ArrowUp className="inline h-3.5 w-3.5 mr-2" />Move Up
        </button>
        <button type="button" className={ITEM} disabled={!canMoveDown} onClick={choose(onMoveDown)}>
          <ArrowDown className="inline h-3.5 w-3.5 mr-2" />Move Down
        </button>
        <button type="button" className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50" onClick={choose(onDelete)}>
          <Trash2 className="inline h-3.5 w-3.5 mr-2" />Delete
        </button>
      </PopoverContent>
    </Popover>
  );
}
